import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import type { Class, Team } from "../../types/tournament";
import type { IDaySchedule, IMatchScheduleItem } from "../../types/schedule";
import { isFreeSlot } from "../../types/schedule";
import { MIN_SCHEDULE_BULK_ADJUST, MAX_SCHEDULE_BULK_SELECTION } from "./scheduleSelectionConstants";
import { flattenScheduledMatches } from "./scheduleOverlap";

export const selectSchedule = (state: RootState) => state.schedule;

export const selectScheduleData = (state: RootState) => state.schedule.schedule;

export const selectDisplaySchedule = (state: RootState) => state.schedule.displaySchedule;

export const selectScheduleConstraints = (state: RootState) => state.schedule.scheduleConstraints;

export const selectScheduleSelectedItem = (state: RootState) => state.schedule.selectedItem;

export const selectScheduleSelectedMatchIds = (state: RootState) =>
    state.schedule.selectedMatchIds;

export const selectScheduleSelectedCount = (state: RootState) =>
    state.schedule.selectedMatchIds.length;

export const selectScheduleBulkAdjustEligible = (state: RootState) =>
    state.schedule.selectedMatchIds.length >= MIN_SCHEDULE_BULK_ADJUST;

export const selectScheduleCurrentFilters = (state: RootState) => state.schedule.currentFilters;

export const selectUnscheduledMatches = (state: RootState) => state.schedule.unscheduledMatches;

/** Match IDs in the unscheduled list eligible for bulk selection (shift+click). */
export const selectBulkSelectableUnscheduledMatchIds = createSelector(
    [selectUnscheduledMatches],
    (unscheduledMatches): number[] => {
        const ids: number[] = [];
        for (const match of unscheduledMatches) {
            ids.push(match.id);
            if (ids.length >= MAX_SCHEDULE_BULK_SELECTION) return ids;
        }
        return ids;
    }
);

/** Selected match IDs that are on the schedule grid. */
export const selectBulkSelectionScheduledIds = createSelector(
    [selectScheduleSelectedMatchIds, selectScheduleData],
    (selectedIds, schedule): number[] => {
        if (selectedIds.length === 0) return [];
        const scheduledSet = new Set(flattenScheduledMatches(schedule).map((m) => m.id));
        return selectedIds.filter((id) => scheduledSet.has(id));
    }
);

/** Selected match IDs in the unscheduled list. */
export const selectBulkSelectionUnscheduledIds = createSelector(
    [selectScheduleSelectedMatchIds, selectUnscheduledMatches],
    (selectedIds, unscheduledMatches): number[] => {
        if (selectedIds.length === 0) return [];
        const unscheduledSet = new Set(unscheduledMatches.map((m) => m.id));
        return selectedIds.filter((id) => unscheduledSet.has(id));
    }
);

export const selectBulkSelectionHasScheduled = createSelector(
    [selectBulkSelectionScheduledIds],
    (scheduledIds) => scheduledIds.length > 0
);

export const selectScheduleIsCreating = (state: RootState) => state.schedule.isCreatingSchedule;

export const selectScheduleLoadedSaved = (state: RootState) => state.schedule.loadedSavedSchedule;

export const selectScheduleIsGeneratingAsync = (state: RootState) => state.schedule.isGeneratingAsync;

export const selectScheduleJobQueuePosition = (state: RootState) => state.schedule.jobQueuePosition;

export const selectScheduleJobEtaSeconds = (state: RootState) => state.schedule.jobEtaSeconds;

export const selectScheduleItemScaleMultiplier = (state: RootState) =>
    state.schedule.scheduleItemScaleMultiplier;

export const selectScheduleColumnWidth = (state: RootState) => state.schedule.scheduleColumnWidth;

export const selectScheduleTournamentInfo = (state: RootState) => state.schedule.tournamentInfo;

export const selectScheduleEditorFullscreen = (state: RootState) =>
    state.schedule.scheduleEditorFullscreen;

export const selectScheduleHasUnpublishedChanges = (state: RootState) =>
    state.schedule.hasUnpublishedChanges;

export const selectScheduleIsFreeEditMode = (state: RootState) => state.schedule.isFreeEditMode;

export const selectScheduleClassIdToObject = (state: RootState) => state.schedule.classIdToObject;

export const selectScheduleStaleFromPlay = (state: RootState) =>
    state.schedule.scheduleStaleFromPlay;

export const selectScheduleDayOptions = (state: RootState): IDaySchedule[] =>
    state.schedule.schedule?.days ?? [];

export type ScheduleTeamFilterOption = {
    label: string;
    value: number;
    logo: string | null;
};

export const selectScheduleTeamFilterOptions = createSelector(
    [selectScheduleData],
    (schedule): ScheduleTeamFilterOption[] => {
        if (!schedule?.days?.length) return [];

        const teamsById = new Map<number, Team>();
        for (const day of schedule.days) {
            for (const court of day.courts) {
                for (const match of court.matches) {
                    if (isFreeSlot(match)) continue;
                    const item = match as IMatchScheduleItem;
                    for (const team of [item.homeTeam, item.awayTeam]) {
                        if (team) teamsById.set(team.id, team);
                    }
                }
            }
        }

        return Array.from(teamsById.values()).map((team) => ({
            label: team.name,
            value: team.id,
            logo: team.logo ?? null,
        }));
    }
);

export const selectScheduleTournamentClasses = (state: RootState): Class[] =>
    state.schedule.tournamentInfo?.classes ?? [];

export const selectScheduleUpdating = (state: RootState) => state.schedule.updatingSchedule;

export const selectSchedulePublishing = (state: RootState) => state.schedule.publishingSchedule;

export const selectScheduleLastDraftSavedAt = (state: RootState) =>
    state.schedule.scheduleLastDraftSavedAt;

export const selectScheduleLastPublishedAt = (state: RootState) =>
    state.schedule.scheduleLastPublishedAt;

export type SchedulePublishStatusChipVariant = "warning" | "success" | "neutral";

export type SchedulePublishStatus = {
    variant: SchedulePublishStatusChipVariant;
    labelKey: string;
    draftSavedAt: string | null;
    publishedAt: string | null;
    isBusy: boolean;
};

export const selectSchedulePublishStatus = createSelector(
    [
        selectScheduleData,
        selectScheduleHasUnpublishedChanges,
        selectScheduleUpdating,
        selectSchedulePublishing,
        selectScheduleLastDraftSavedAt,
        selectScheduleLastPublishedAt,
    ],
    (
        schedule,
        hasUnpublishedChanges,
        updating,
        publishing,
        draftSavedAt,
        publishedAt
    ): SchedulePublishStatus => {
        const isBusy = updating || publishing;
        if (!schedule) {
            return {
                variant: "neutral",
                labelKey: "",
                draftSavedAt,
                publishedAt,
                isBusy,
            };
        }
        if (hasUnpublishedChanges) {
            return {
                variant: "warning",
                labelKey: "creativeDetail-scheduleStatusUnpublished",
                draftSavedAt,
                publishedAt,
                isBusy,
            };
        }
        return {
            variant: "success",
            labelKey: "creativeDetail-scheduleStatusPublished",
            draftSavedAt,
            publishedAt,
            isBusy,
        };
    }
);

export const selectScheduleCanUndo = (state: RootState) =>
    state.schedule.scheduleUndoStack.length > 0;

export const selectScheduleCanRedo = (state: RootState) =>
    state.schedule.scheduleRedoStack.length > 0;

export const selectScheduleMutationBlocked = createSelector(
    [
        selectDisplaySchedule,
        selectScheduleConstraints,
        selectScheduleHasUnpublishedChanges,
        selectScheduleIsFreeEditMode,
    ],
    (displaySchedule, scheduleConstraints, hasUnpublishedChanges, isFreeEditMode) =>
        Boolean(displaySchedule && scheduleConstraints && !hasUnpublishedChanges && !isFreeEditMode)
);